import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  IconButton,
  CircularProgress,
  Divider,
  Paper,
  Container,
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  PhotoCamera as PhotoCameraIcon,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { settingsService } from './settings.service';
import type { Setting, SocialLink } from './settings.dto';

const Settings = () => {
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    phone: '',
    email: '',
    fax: '',
    telephone: '',
    footerFax: '',
    footerTelephone: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    description: '',
    mapiframe: '',
    copyright: '',
    logo: '',
    ipAddress: '',
  });
  // const [newSocialLink, setNewSocialLink] = useState<SocialLink>({
  //   title: '',
  //   url: '',
  //   icon: '',
  // });
  // const [socialIconPreview, setSocialIconPreview] = useState<string>('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  useEffect(() => {
    settingsService.setLogoutCallback(logout);
    fetchSettings();
  }, [logout]);

  const fetchSettings = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data: Setting[] = await settingsService.getAllSettings(token);

      const settingsObj: Record<string, string> = {};
      data.forEach(setting => {
        settingsObj[setting.key] = setting.value;
      });

      setSettings({
        phone: settingsObj.phone || '',
        email: settingsObj.email || '',
        fax: settingsObj.fax || '',
        telephone: settingsObj.telephone || '',
        footerFax: settingsObj.footerFax || '',
        footerTelephone: settingsObj.footerTelephone || '',
        address: settingsObj.address || '',
        city: settingsObj.city || '',
        province: settingsObj.province || '',
        postalCode: settingsObj.postalCode || '',
        description: settingsObj.description || '',
        // socialLinks: settingsObj.socialLinks
        //   ? JSON.parse(settingsObj.socialLinks)
        //   : [],
        mapiframe: settingsObj.mapiframe || '',
        copyright: settingsObj.copyright || '',
        logo: settingsObj.logo || '',
        ipAddress: settingsObj.ipAddress || '',
      });

      // Set logo preview if exists
      if (settingsObj.logo) {
        setLogoPreview(settingsObj.logo);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to fetch settings',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (field: keyof typeof settings) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = event.target.value;

      // Special handling for phone field - only allow + and numbers
      if (field === 'phone') {
        value = value.replace(/[^+0-9]/g, '');
        // Ensure + can only be at the start
        if (value.includes('+')) {
          const plusCount = (value.match(/\+/g) || []).length;
          if (plusCount > 1 || value.indexOf('+') !== 0) {
            value = value.replace(/\+/g, '');
            if (event.target.value.startsWith('+')) {
              value = '+' + value;
            }
          }
        }
      }

      setSettings({ ...settings, [field]: value });
    };

  // const handleSocialIconChange = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       const preview = reader.result as string;
  //       setSocialIconPreview(preview);
  //       setNewSocialLink({ ...newSocialLink, icon: preview });
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };

  // const handleRemoveSocialIcon = () => {
  //   setSocialIconPreview('');
  //   setNewSocialLink({ ...newSocialLink, icon: '' });
  // };

  // const handleAddSocialLink = () => {
  //   if (newSocialLink.title && newSocialLink.url) {
  //     setSettings({
  //       ...settings,
  //       socialLinks: [...settings.socialLinks, newSocialLink],
  //     });
  //     setNewSocialLink({ title: '', url: '', icon: '' });
  //     setSocialIconPreview('');
  //   }
  // };

  // const handleRemoveSocialLink = (index: number) => {
  //   const updatedLinks = settings.socialLinks.filter((_, i) => i !== index);
  //   setSettings({ ...settings, socialLinks: updatedLinks });
  // };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setSettings({ ...settings, logo: '' });
  };

  const handleSave = async () => {
    if (!token) return;

    try {
      setLoading(true);

      // Create FormData with all settings
      const formData = new FormData();
      formData.append('phone', settings.phone);
      formData.append('fax', settings.fax);
      formData.append('telephone', settings.telephone);
      formData.append('footerFax', settings.footerFax);
      formData.append('footerTelephone', settings.footerTelephone);
      formData.append('address', settings.address);
      formData.append('city', settings.city);
      formData.append('province', settings.province);
      formData.append('postalCode', settings.postalCode);
      formData.append('email', settings.email);
      formData.append('description', settings.description);
      // formData.append('socialLinks', JSON.stringify(settings.socialLinks));
      formData.append('mapiframe', settings.mapiframe);
      formData.append('copyright', settings.copyright);
      formData.append('ipAddress', settings.ipAddress);

      // If logo file is selected, append it; otherwise append the existing URL
      if (logoFile) {
        formData.append('logo', logoFile);
      } else if (settings.logo) {
        formData.append('logo', settings.logo);
      }

      // Call the service to update settings
      await settingsService.updateAllSettings(token, formData);

      setSnackbar({
        open: true,
        message: 'Settings updated successfully.',
        severity: 'success',
      });

      // Refresh settings after update
      await fetchSettings();
    } catch (err) {
      console.error('Error updating settings:', err);
      setSnackbar({
        open: true,
        message:
          err instanceof Error ? err.message : 'Failed to update settings',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading settings...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <SettingsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h4" component="h1">
              Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage site-wide settings, contact information, and branding
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Logo Upload Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Logo
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: logoPreview ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: logoPreview ? 0 : 4,
                    textAlign: 'center',
                    backgroundColor: '#000000',
                    mb: 2,
                    // minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {logoPreview ? (
                    <>
                      <Box
                        component="img"
                        src={logoPreview}
                        alt="Logo preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          p: 2,
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveLogo}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                          },
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <Box>
                      <SettingsIcon
                        sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Upload site logo
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  component="label"
                  fullWidth
                >
                  {logoPreview ? 'Change Logo' : 'Upload Logo'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleLogoChange}
                  />
                </Button>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: 'center' }}
                >
                  Recommended: PNG or JPG format, transparent background
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="div">
                  <Grid container spacing={3}>
                    {/* Contact Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Contact Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={settings.phone}
                        onChange={handleChange('phone')}
                        placeholder="+542121516"
                        helperText="Format: +542121516"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={settings.email}
                        onChange={handleChange('email')}
                        placeholder="support@example.com"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Fax"
                        value={settings.fax}
                        onChange={handleChange('fax')}
                        placeholder="02121516"
                        helperText="Format: 02121516"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Telephone"
                        value={settings.telephone}
                        onChange={handleChange('telephone')}
                        placeholder="02121516"
                        helperText="Format: 02121516"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Footer Fax"
                        value={settings.footerFax}
                        onChange={handleChange('footerFax')}
                        placeholder="02121516"
                        helperText="Format: 02121516"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Footer Telephone"
                        value={settings.footerTelephone}
                        onChange={handleChange('footerTelephone')}
                        placeholder="02121516"
                        helperText="Format: 02121516"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={settings.address}
                        onChange={handleChange('address')}
                        placeholder="Enter street address, building number..."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={settings.city}
                        onChange={handleChange('city')}
                        placeholder="Enter city"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Province"
                        value={settings.province}
                        onChange={handleChange('province')}
                        placeholder="Enter province"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="postalCode"
                        value={settings.postalCode}
                        onChange={handleChange('postalCode')}
                        placeholder="Enter postalCode"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={settings.description}
                        onChange={handleChange('description')}
                        placeholder="Enter site description..."
                      />
                    </Grid>

                    {/* Map & Copyright */}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Map & Copyright
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Map iFrame URL"
                        value={settings.mapiframe}
                        onChange={handleChange('mapiframe')}
                        placeholder="https://maps.google.com/maps?..."
                        helperText="Embed Google Maps iframe URL"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Copyright Text"
                        value={settings.copyright}
                        onChange={handleChange('copyright')}
                        placeholder="Copyright © 2025 Company. All rights reserved"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="IP Adresses"
                      multiline
                      rows={4}
                      value={settings.ipAddress}
                      onChange={handleChange('ipAddress')}
                      placeholder="Enter the IP addresses allowed to use the staging payment gateway."
                    />
                  </Grid>
                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <SaveIcon />
                      }
                      onClick={handleSave}
                      disabled={loading}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {loading ? 'Saving...' : 'Save Settings'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;
